import { EnvVarPopover } from "@/components/EnvVarPopover";
import { Button } from "@/components/ui/button";
import {
  FpDropdownMenu,
  FpDropdownMenuContent,
  FpDropdownMenuItem,
  FpDropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ResizableHandle, ResizablePanel } from "@/components/ui/resizable";
import {
  FpTabs,
  FpTabsContent,
  FpTabsList,
  FpTabsTrigger,
} from "@/components/ui/tabs";
import { useHandler } from "@/hooks";
import { useActiveChatId } from "@/lib/hooks/useActiveChatId";
import { useActiveVersionNumber } from "@/lib/hooks/useActiveVersionNumber";
import { useMessageStore } from "@/state/stores";
import {
  BrainIcon,
  ChevronDownIcon,
  CloudIcon,
  CodeIcon,
  CopyIcon,
  ExternalLinkIcon,
  EyeIcon,
  GlobeIcon,
  ShapesIcon,
} from "lucide-react";
import { FileAttachmentView } from "../../AttachmentId/FileAttachmentView";
import { PreviewView } from "../PreviewView";
import { useNavigateToLatest } from "../hooks";
import { useFileOperations } from "../hooks/useFileOperations";
import { useLatestDeploymentUrl } from "../hooks/useLatestDeploymentUrl";
import { useTabNavigation } from "../hooks/useTabNavigation";
import { SpecViewer } from "./SpecViewer";
import { VersionDropdown } from "./VersionDropdown";

export function AttachmentsSection() {
  const chatId = useActiveChatId();
  const versionNumber = useActiveVersionNumber();

  useNavigateToLatest();

  const hasMultipleVersions = useMessageStore(
    (state) => Object.keys(state.versionInformation).length > 1,
  );

  const tabNavigation = useTabNavigation({
    chatId: chatId ?? "",
    versionNumber,
  });
  const { computedActiveTab, navigateTab } = tabNavigation;

  const { downloadFiles } = useFileOperations();
  const handleDownloadFiles = useHandler(() => {
    downloadFiles(chatId);
  });

  const latestDeployUrl = useLatestDeploymentUrl();

  const handleCopyUrl = useHandler(async () => {
    if (latestDeployUrl) {
      try {
        await navigator.clipboard.writeText(latestDeployUrl);
      } catch (error) {
        console.error("Failed to copy URL:", error);
      }
    }
  });

  return (
    <>
      <ResizableHandle />
      <ResizablePanel defaultSize={50}>
        <FpTabs
          className="h-full flex flex-col bg-background"
          value={computedActiveTab}
          onValueChange={navigateTab}
        >
          <header className="border-b shrink-0">
            <FpTabsList className="flex items-center justify-between h-12 px-4">
              <div className="flex gap-1">
                <FpTabsTrigger value="brainstorm">
                  <BrainIcon className="w-4 h-4" /> Brainstorm
                </FpTabsTrigger>
                <FpTabsTrigger value="code">
                  <CodeIcon className="w-4 h-4" /> Code
                </FpTabsTrigger>
                <FpTabsTrigger value="preview">
                  <EyeIcon className="w-4 h-4" /> Preview
                </FpTabsTrigger>
              </div>
              <div className="flex items-center gap-2">
                {hasMultipleVersions && <VersionDropdown />}

                {chatId && <EnvVarPopover chatId={chatId} />}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleDownloadFiles}
                >
                  <ShapesIcon />
                  Download
                </Button>

                <FpDropdownMenu>
                  <FpDropdownMenuTrigger asChild>
                    <Button variant="primary" size="sm">
                      <CloudIcon />
                      Deploy
                      <ChevronDownIcon className="w-3 h-3" />
                    </Button>
                  </FpDropdownMenuTrigger>
                  <FpDropdownMenuContent align="end">
                    {latestDeployUrl ? (
                      <>
                        <FpDropdownMenuItem className="hover:bg-muted focus:bg-muted cursor-pointer">
                          <a
                            href={latestDeployUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 justify-between w-full"
                          >
                            <GlobeIcon className="w-4 h-4" />
                            <span className="text-xs">{latestDeployUrl}</span>
                            <ExternalLinkIcon className="w-4 h-4" />
                          </a>
                        </FpDropdownMenuItem>
                        <FpDropdownMenuItem
                          onClick={handleCopyUrl}
                          className="hover:bg-muted focus:bg-muted cursor-pointer"
                        >
                          <CopyIcon className="w-4 h-4" />
                          Copy URL
                        </FpDropdownMenuItem>
                      </>
                    ) : (
                      <FpDropdownMenuItem disabled>
                        <CloudIcon className="w-4 h-4" />
                        No deployments yet
                      </FpDropdownMenuItem>
                    )}
                  </FpDropdownMenuContent>
                </FpDropdownMenu>
              </div>
            </FpTabsList>
          </header>
          <FpTabsContent value="brainstorm" className="flex-1 overflow-hidden">
            <SpecViewer />
          </FpTabsContent>
          <FpTabsContent value="code" className="flex-1 overflow-hidden">
            <div className="h-full flex flex-col overflow-y-auto bg-background">
              <FileAttachmentView />
            </div>
          </FpTabsContent>
          <FpTabsContent value="preview" className="flex-1 overflow-hidden">
            <PreviewView />
          </FpTabsContent>
        </FpTabs>
      </ResizablePanel>
    </>
  );
}
